import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageShell, SkeletonCard, ErrorMessage } from '../components/Layout';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { useFlowClient } from '../hooks/useFlowClient';
import { daysUntil } from '../utils/dateUtils';
import { getFloatTier } from '../utils/riskUtils';
import { isAdminEmail } from '../utils/adminAllowlist';
import { getDemoSessionToken, setDemoSessionToken } from '../utils/demoSession';
import { computeStatus, statusBadgeClass } from '../components/WeeklyGrid';
import { Project } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? '/api';

function daysSinceUpload(p: Project): number | null {
  if (!p.last_upload_date) return null;
  const d = daysUntil(p.last_upload_date);
  return d !== null ? -d : null;
}

function relativeUpload(p: Project): string {
  const d = daysSinceUpload(p);
  if (d === null) return '—';
  if (d === 0) return 'today';
  if (d === 1) return '1 day ago';
  return `${d} days ago`;
}

function fmtCompletion(p: Project): string {
  if (!p.cpm_completion_date) return '—';
  const [y, m, d] = String(p.cpm_completion_date).substring(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const isStale = (p: Project) => { const d = daysSinceUpload(p); return d !== null && d > 7; };

// Demo mode: the sample project's upload date is fixed, so "not updated in N days" and
// "Last upload N days ago" would only grow stale-looking over time. Hide upload age there.
// A demo session is fixed for the page's lifetime (App.tsx reads it the same way).
const hideUploadAge = !!getDemoSessionToken();

// Status behind the coloured left edge: at risk (negative float), watch (thin float or a
// stale schedule), on track. Same rules as the KPI counts below.
type Tone = 'crit' | 'watch' | 'ok';
function projectTone(p: Project): Tone {
  const f = Number(p.float_days_remaining ?? 0);
  if (f < 0) return 'crit';
  if (f < 5 || (!hideUploadAge && isStale(p))) return 'watch';
  return 'ok';
}
const EDGE: Record<Tone, string> = { crit: 'var(--edge-crit)', watch: 'var(--edge-watch)', ok: 'var(--edge-ok)' };
const TINT: Record<Tone, string> = { crit: 'var(--crit-bg)', watch: 'var(--watch-bg)', ok: 'var(--ok-bg)' };

// Layout is chosen from how many projects the user can see.
type PortfolioMode = 'empty' | 'single' | 'few' | 'many';
export function getPortfolioMode(count: number): PortfolioMode {
  if (count <= 0) return 'empty';
  if (count === 1) return 'single';
  if (count <= 3) return 'few';
  return 'many';
}

// Project status pill — unchanged from before.
const StatusPill: React.FC<{ project: Project }> = ({ project }) => {
  const isAtRisk = Number(project.float_days_remaining ?? 0) < 0;
  return (
    <span className="pill shrink-0" style={{
      background: isAtRisk ? 'var(--crit-bg)' : 'var(--ok-bg)',
      color: isAtRisk ? 'var(--crit-text)' : 'var(--ok-text)',
    }}>
      {isAtRisk ? 'At Risk' : 'On Track'}
    </span>
  );
};

// ≥44px touch targets below xl; compact on desktop.
const BTN = 'inline-flex items-center justify-center rounded-[8px] px-3 text-[13px] font-semibold whitespace-nowrap min-h-[44px] xl:min-h-[36px] transition-colors';
const BTN_PRIMARY = `${BTN} bg-accent text-white hover:bg-link`;
const BTN_SECONDARY = `${BTN} bg-white border border-line text-ink hover:bg-ground`;

// The one project-list page. Every login lands here (App.tsx), and /projects redirects
// here for old bookmarks.
const Portfolio: React.FC = () => {
  const navigate = useNavigate();
  const { projects, setSelectedProject, userDataLoading, userDataError } = useApp();
  const [searchQuery, setSearchQuery] = useState('');

  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });

  const mode = getPortfolioMode(projects.length);

  const atRiskCount = projects.filter(p => (p.float_days_remaining ?? 0) < 0).length;
  const onWatchCount = projects.filter(p => {
    const f = p.float_days_remaining ?? 0;
    return f >= 0 && f < 5;
  }).length;
  const staleCount = projects.filter(isStale).length;

  const allStats = [
    { icon: 'ti-layout-dashboard', value: projects.length, label: 'Total projects', bg: 'var(--table-head-bg)', color: 'var(--muted)' },
    { icon: 'ti-alert-triangle', value: atRiskCount, label: 'At risk', bg: 'var(--crit-bg)', color: 'var(--crit-text)' },
    { icon: 'ti-eye', value: onWatchCount, label: 'On watch', bg: 'var(--watch-bg)', color: 'var(--watch-text)' },
    { icon: 'ti-clock', value: staleCount, label: 'Schedules stale', bg: 'var(--watch-bg)', color: 'var(--watch-text)' },
  ];
  const stats = mode === 'few' ? allStats.slice(0, 3) : allStats;

  const filteredProjects = mode === 'many'
    ? projects.filter(p => (p.project_name ?? '').toLowerCase().includes(searchQuery.toLowerCase()))
    : projects;

  const open = (project: Project, page: 'dashboard' | 'ask' | 'lookahead' | 'upload' = 'dashboard') => {
    setSelectedProject(project);
    navigate(`/project/${project.id}/${page}`);
  };

  return (
    <PageShell className="pt-5 sm:pt-6 pb-10">
      {userDataLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <SkeletonCard key={i} />)}
        </div>
      ) : userDataError ? (
        <ErrorMessage message={userDataError} />
      ) : (
        <>
          <div className="mb-4">
            <h1 className="h1-page">My Projects</h1>
            <div className="text-[15px] text-muted mt-0.5">
              {today} · {projects.length} active {projects.length === 1 ? 'project' : 'projects'}
            </div>
          </div>

          {mode === 'empty' && <EmptyState />}

          {mode === 'single' && <SingleProjectCard project={projects[0]} onOpen={open} />}

          {(mode === 'few' || mode === 'many') && (
            <>
              {/* KPI strip — 2 across under 900px; 3 (few) or 4 (many) across above. */}
              <div className={`grid grid-cols-2 gap-2 mb-4 ${mode === 'few' ? 'sm:grid-cols-3' : 'min-[900px]:grid-cols-4'}`}>
                {stats.map((s, i) => (
                  <div key={i} className={`bg-white border border-line rounded-[10px] flex items-center gap-2.5 min-w-0 ${mode === 'few' ? 'px-3 py-2.5' : 'px-3.5 py-3'}`}>
                    <div className="w-8 h-8 rounded-[8px] flex items-center justify-center shrink-0" style={{ background: s.bg }}>
                      <i className={`ti ${s.icon}`} style={{ color: s.color, fontSize: '16px' }} aria-hidden="true" />
                    </div>
                    <div className="min-w-0">
                      <div className="kpi-num" style={{ color: s.color, lineHeight: 1 }}>{s.value}</div>
                      <div className="text-[11px] text-muted mt-0.5">{s.label}</div>
                    </div>
                  </div>
                ))}
              </div>

              {mode === 'many' && (
                <div className="mb-3">
                  <input
                    type="text"
                    placeholder="Search projects..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    // 16px text on phones — iOS Safari zooms the page when focusing an input under 16px.
                    className="w-full bg-white border border-line rounded-[8px] px-3 py-2.5 sm:py-2 text-base sm:text-xs outline-none min-h-[44px] sm:min-h-0"
                  />
                </div>
              )}

              {filteredProjects.length === 0 ? (
                <div className="text-center py-10 px-5 text-muted text-[13px]">
                  No projects match "{searchQuery}"
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredProjects.map(project => <ProjectRow key={project.id} project={project} onOpen={open} />)}
                </div>
              )}
            </>
          )}
        </>
      )}
    </PageShell>
  );
};

/* ─── Standard project row (2+ projects) ─── */
const ProjectRow: React.FC<{ project: Project; onOpen: (p: Project, page?: 'dashboard' | 'ask' | 'lookahead' | 'upload') => void }> = ({ project, onOpen }) => {
  const worstFloat = Number(project.float_days_remaining ?? 0);
  const sinceUpload = daysSinceUpload(project);
  const tone = projectTone(project);

  return (
    <div
      onClick={() => onOpen(project)}
      className="bg-white border border-line rounded-[12px] overflow-hidden cursor-pointer transition-colors hover:border-accent"
      style={{ borderLeft: `4px solid ${EDGE[tone]}` }}
    >
      {/* Phone/tablet: stacked card. lg+: one row. */}
      <div className="px-4 py-3.5 flex flex-col gap-3 lg:grid lg:grid-cols-[minmax(0,1fr)_auto] lg:gap-4 lg:items-center">
        <div className="min-w-0">
          <div className="card-title break-anywhere mb-[3px]">{project.project_name}</div>
          <div className="text-[11px] text-muted flex gap-2.5 flex-wrap">
            {project.gc_company && (
              <span className="flex items-center gap-1 min-w-0 break-anywhere">
                <i className="ti ti-building text-faint" style={{ fontSize: '11px' }} aria-hidden="true" />
                {project.gc_company}
              </span>
            )}
            {!hideUploadAge && (
              <span className="flex items-center gap-1">
                <i className="ti ti-calendar text-faint" style={{ fontSize: '11px' }} aria-hidden="true" />
                Updated {relativeUpload(project)}
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
          <div className="lg:text-right">
            <div className="kpi-num" style={{ color: worstFloat < 0 ? 'var(--crit-text)' : 'var(--ok-text)', lineHeight: 1 }}>
              {worstFloat}d
            </div>
            <div className="text-[11px] text-muted mt-px">Project float</div>
          </div>
          <div className="lg:text-right">
            <div className="text-[11px] text-muted">Completion</div>
            <div className="text-[12px] font-medium text-text">{fmtCompletion(project)}</div>
          </div>
          <StatusPill project={project} />

          {/* Full-width button row when stacked; inline from lg up. */}
          <div className="grid grid-cols-3 gap-1.5 w-full lg:flex lg:w-auto" onClick={e => e.stopPropagation()}>
            <button onClick={() => onOpen(project)} className={BTN_PRIMARY}>View dashboard</button>
            <button onClick={() => onOpen(project, 'ask')} className={BTN_SECONDARY}>Ask AI</button>
            {/* Viewers can't upload — hidden rather than shown-then-403. */}
            {project.role_on_project !== 'Viewer' && (
              <button onClick={() => onOpen(project, 'upload')} className={BTN_SECONDARY}>Upload</button>
            )}
          </div>
        </div>
      </div>

      {!hideUploadAge && sinceUpload !== null && sinceUpload > 7 && <StaleBanner days={sinceUpload} />}
    </div>
  );
};

const StaleBanner: React.FC<{ days: number }> = ({ days }) => (
  <div className="flex items-center gap-1.5 px-4 py-1.5 text-[11px] text-watch-text bg-watch-bg border-t border-watch-border">
    <i className="ti ti-alert-triangle" style={{ fontSize: '12px' }} aria-hidden="true" />
    Schedule not updated in {days} days — upload a new version
  </div>
);

/* ─── Single project: one expanded card ─── */
interface TopActivity {
  activity_id: string;
  activity_name: string;
  total_float: number;
  is_critical?: boolean;
  actual_start?: string;
  actual_finish?: string;
}

const SingleProjectCard: React.FC<{ project: Project; onOpen: (p: Project, page?: 'dashboard' | 'ask' | 'lookahead' | 'upload') => void }> = ({ project, onOpen }) => {
  const flowClient = useFlowClient();
  const { userEmail } = useAuth();
  const [activities, setActivities] = useState<TopActivity[] | null>(null);
  const [activitiesError, setActivitiesError] = useState(false);

  // Same read the Dashboard does — only needed in this one-project view.
  useEffect(() => {
    if (!userEmail) return;
    let cancelled = false;
    setActivities(null);
    setActivitiesError(false);
    flowClient.ask(userEmail, project.id, 'get_project_activities')
      .then(res => {
        const parsed = JSON.parse(res.answer ?? '{}');
        const raw: any[] = parsed.activities ?? [];
        if (!cancelled) setActivities(raw.map(a => ({ ...a, total_float: Number(a.total_float ?? a.float_days ?? 0), is_critical: a.is_critical === true })));
      })
      .catch(() => { if (!cancelled) setActivitiesError(true); });
    return () => { cancelled = true; };
  }, [project.id, userEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  const tone = projectTone(project);
  const worstFloat = Number(project.float_days_remaining ?? 0);
  const sinceUpload = daysSinceUpload(project);
  const canUpload = project.role_on_project !== 'Viewer';

  const open = activities?.filter(a => !a.actual_finish) ?? [];
  const criticalCount = open.filter(a => a.is_critical).length;
  const top3 = [...open].sort((a, b) => a.total_float - b.total_float).slice(0, 3);

  const tiles = [
    { label: 'Project float', value: `${worstFloat}d`, color: worstFloat < 0 ? 'var(--crit-text)' : 'var(--ok-text)' },
    { label: 'Completion date', value: fmtCompletion(project), color: 'var(--ink)' },
    { label: 'Critical activities', value: activities ? String(criticalCount) : activitiesError ? '—' : '…', color: criticalCount > 0 ? 'var(--crit-text)' : 'var(--ink)' },
    { label: 'Last upload', value: relativeUpload(project), color: 'var(--ink)' },
  ].filter(t => !(hideUploadAge && t.label === 'Last upload'));

  return (
    <div className="bg-white border border-line rounded-[12px] overflow-hidden" style={{ borderLeft: `4px solid ${EDGE[tone]}` }}>
      {/* Header strip, tinted with the status background */}
      <div className="px-4 sm:px-5 py-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between" style={{ background: TINT[tone] }}>
        <div className="flex flex-wrap items-start gap-x-2.5 gap-y-1.5 min-w-0 lg:pr-3">
          <h2 className="font-display font-bold text-[20px] leading-tight text-ink break-anywhere min-w-0">{project.project_name}</h2>
          <span className="mt-0.5 shrink-0"><StatusPill project={project} /></span>
        </div>
        {/* Phone: View dashboard full width, then the rest side by side. */}
        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap lg:flex-nowrap shrink-0">
          <button onClick={() => onOpen(project)} className={`${BTN_PRIMARY} col-span-2`}>View dashboard</button>
          <button onClick={() => onOpen(project, 'ask')} className={BTN_SECONDARY}>Ask AI</button>
          {canUpload && <button onClick={() => onOpen(project, 'upload')} className={BTN_SECONDARY}>Upload</button>}
          <button onClick={() => onOpen(project, 'lookahead')} className={`${BTN_SECONDARY} ${canUpload ? 'col-span-2' : ''}`}>Lookahead</button>
        </div>
      </div>

      {!hideUploadAge && sinceUpload !== null && sinceUpload > 7 && <StaleBanner days={sinceUpload} />}

      <div className="p-4 sm:p-5 space-y-5">
        {/* Stats: 2×2 on phones, 4 across from md */}
        <div className={`grid grid-cols-2 gap-2 ${tiles.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-4'}`}>
          {tiles.map((t, i) => (
            // With 3 tiles (demo), the last one spans the phone row instead of sitting alone.
            <div key={t.label} className={`rounded-[10px] border border-line px-3.5 py-3 min-w-0 ${tiles.length === 3 && i === 2 ? 'col-span-2 md:col-span-1' : ''}`}>
              <div className="text-[11px] text-muted">{t.label}</div>
              <div className="kpi-num mt-1 break-anywhere" style={{ color: t.color, lineHeight: 1.1 }}>{t.value}</div>
            </div>
          ))}
        </div>

        <div>
          <h3 className="card-title mb-2">Top critical activities</h3>
          {activities === null && !activitiesError ? (
            <div className="space-y-1.5">
              {[1, 2, 3].map(i => <div key={i} className="h-11 rounded-[8px] bg-ground animate-pulse" />)}
            </div>
          ) : activitiesError ? (
            <p className="text-[13px] text-muted">Couldn't load activities — open the Dashboard to see them.</p>
          ) : top3.length === 0 ? (
            <p className="text-[13px] text-muted">No open activities.</p>
          ) : (
            <ul className="space-y-1.5">
              {top3.map(a => {
                const actTone: Tone = a.is_critical ? 'crit' : getFloatTier(a.total_float) === 'Watch' ? 'watch' : 'ok';
                const status = computeStatus(a.actual_start ?? '', a.actual_finish ?? '');
                return (
                  <li
                    key={a.activity_id}
                    className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-[8px] border border-line px-3 py-2"
                    style={{ borderLeft: `3px solid ${EDGE[actTone]}` }}
                  >
                    <div className="min-w-0 flex-1 basis-[200px]">
                      <div className="text-[14px] font-semibold text-text break-anywhere">{a.activity_name}</div>
                      <div className="text-[11px] font-mono text-muted break-anywhere">{a.activity_id}</div>
                    </div>
                    <span className="text-[13px] font-semibold" style={{ color: a.total_float < 0 ? 'var(--crit-text)' : 'var(--ink)' }}>
                      {a.total_float}d float
                    </span>
                    <span className={`pill ${statusBadgeClass(status)}`}>{status}</span>
                  </li>
                );
              })}
            </ul>
          )}
          <button onClick={() => onOpen(project)} className="mt-3 text-[13px] font-semibold text-link hover:underline min-h-[44px] xl:min-h-0">
            View all in Dashboard →
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── No projects yet ─── */
const EmptyState: React.FC = () => {
  const navigate = useNavigate();
  const { userEmail } = useAuth();
  const isAdmin = isAdminEmail(userEmail);
  const [note, setNote] = useState<string | null>(null);
  const [demoLoading, setDemoLoading] = useState(false);

  // Uploads attach to a project, and projects are created in the Admin Center — so
  // admins go there; anyone else is told who sets their first project up.
  const handleUpload = () => {
    if (isAdmin) navigate('/admin');
    else setNote('Your Flotillo administrator creates your first project — then you can upload its schedule here.');
  };

  // Same sample-schedule session the landing page starts.
  const startSample = async () => {
    setDemoLoading(true);
    setNote(null);
    try {
      const res = await fetch(`${API_BASE_URL}/demo/session`, { method: 'POST' });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setDemoSessionToken(data.token);
      window.location.href = '/project/DEMO-001/dashboard';
    } catch {
      setNote('Could not start the sample schedule. Please try again.');
      setDemoLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-[520px] bg-white border border-line rounded-[12px] px-6 py-10 text-center mt-4">
      <div className="mx-auto w-12 h-12 rounded-[12px] bg-tint flex items-center justify-center mb-4">
        <svg className="w-6 h-6 text-accent" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M5 19l2.757 -7.351a1 1 0 0 1 .936 -.649h12.307a1 1 0 0 1 .986 1.164l-.996 5.211a2 2 0 0 1 -1.964 1.625h-14.026a2 2 0 0 1 -2 -2v-11a2 2 0 0 1 2 -2h4l3 3h7a2 2 0 0 1 2 2v2" />
        </svg>
      </div>
      <h2 className="font-display font-bold text-[20px] text-ink">No projects yet</h2>
      <p className="text-[14px] text-muted mt-1.5">
        Flotillo reads your Primavera P6 .xer schedule and shows where float is running out.
      </p>
      <button onClick={handleUpload} className={`${BTN_PRIMARY} mt-5 w-full sm:w-auto px-5`}>
        Upload your first schedule
      </button>
      <p className="text-[12px] text-faint mt-2">Accepts Primavera P6 .xer files</p>
      {note && <p className="text-[13px] text-watch-text mt-3">{note}</p>}
      <button
        onClick={startSample}
        disabled={demoLoading}
        className="mt-4 text-[13px] font-semibold text-link hover:underline disabled:opacity-60 min-h-[44px] xl:min-h-0"
      >
        {demoLoading ? 'Starting sample…' : 'Or try a sample schedule'}
      </button>
    </div>
  );
};

export default Portfolio;
